import React, { useEffect, useState } from 'react';

// Branded Compass loading state — used both as the lazy-chunk Suspense fallback
// (so it must be self-contained: inline styles + injected keyframes, no reliance
// on compass.css being loaded yet) and while the projects/first data load. The
// status lines are cosmetic — they narrate what's happening (roughly) so the wait
// feels intentional rather than broken.

const STEPS = [
  'Waking Bravo up…',
  'Connecting to the data warehouse…',
  'Checking which clients you can see…',
  'Pulling your Compass projects…',
  'Assembling your workspace…',
];

const KEYFRAMES = `
@keyframes cpl-spin { to { transform: rotate(360deg); } }
@keyframes cpl-core { 0%,100% { transform: scale(0.72); opacity: 0.85 } 50% { transform: scale(1); opacity: 1 } }
@keyframes cpl-ring { 0% { transform: scale(0.45); opacity: 0.55 } 100% { transform: scale(1.75); opacity: 0 } }
@keyframes cpl-shimmer { 0% { transform: translateX(-130%) } 100% { transform: translateX(130%) } }
@keyframes cpl-fade { from { opacity: 0; transform: translateY(5px) } to { opacity: 1; transform: none } }
`;

function useKeyframes() {
  useEffect(() => {
    if (document.getElementById('cpl-keyframes')) return;
    const el = document.createElement('style');
    el.id = 'cpl-keyframes';
    el.textContent = KEYFRAMES;
    document.head.appendChild(el);
  }, []);
}

const GREEN = '#01FF85';
const INK = '#141615';
const BG = '#f0eee9';

export default function CompassLoader({ note }) {
  useKeyframes();
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % STEPS.length), 1500);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{
      minHeight: '68vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 26, background: BG, padding: 40, textAlign: 'center',
    }}>
      {/* pulsing rings + spinning arc + green core */}
      <div style={{ position: 'relative', width: 96, height: 96 }}>
        {[0, 0.6, 1.2].map((d) => (
          <span key={d} style={{
            position: 'absolute', inset: 0, borderRadius: '50%', border: `1.5px solid ${GREEN}`,
            animation: `cpl-ring 2.1s ${d}s cubic-bezier(0.2,0.6,0.35,1) infinite`,
          }} />
        ))}
        <span style={{
          position: 'absolute', inset: 8, borderRadius: '50%',
          border: `2.5px solid rgba(20,22,21,0.12)`, borderTopColor: INK,
          animation: 'cpl-spin 0.9s linear infinite',
        }} />
        <span style={{
          position: 'absolute', inset: 34, borderRadius: '50%', background: GREEN,
          boxShadow: `0 0 18px ${GREEN}`, animation: 'cpl-core 1.5s ease-in-out infinite',
        }} />
      </div>

      <div>
        <div style={{
          fontFamily: '"Bricolage Grotesque", system-ui, sans-serif', fontWeight: 800,
          textTransform: 'uppercase', letterSpacing: '0.01em', fontSize: 22, color: INK, margin: '0 0 12px',
        }}>
          Bravo is getting your workspace ready
        </div>
        {/* cycling status line — key forces the fade-in each change */}
        <div key={i} style={{
          fontFamily: '"Space Mono", ui-monospace, monospace', fontSize: 12.5, letterSpacing: '0.06em',
          textTransform: 'uppercase', color: 'rgba(20,22,21,0.55)', animation: 'cpl-fade 0.4s ease both',
        }}>
          {note || STEPS[i]}
        </div>
      </div>

      {/* indeterminate shimmer bar */}
      <div style={{
        width: 220, height: 4, borderRadius: 999, background: 'rgba(20,22,21,0.08)', overflow: 'hidden', position: 'relative',
      }}>
        <span style={{
          position: 'absolute', top: 0, left: 0, height: '100%', width: '45%', borderRadius: 999,
          background: `linear-gradient(90deg, transparent, ${GREEN}, transparent)`,
          animation: 'cpl-shimmer 1.3s ease-in-out infinite',
        }} />
      </div>
    </div>
  );
}
